(() => {
  if (window.fteCartDeliverySheet) return;
  window.fteCartDeliverySheet = true;

  const mobileMedia = window.matchMedia('(max-width: 767.98px)');
  const rootSelector = 'cart-drawer#CartDrawer #ot-dingdoong-root-cart-drawer';
  const expandedClass = 'fte-delivery-sheet-expanded';
  const collapsedClass = 'fte-delivery-sheet-collapsed';
  const draggingClass = 'fte-delivery-sheet-dragging';
  const dragZoneHeight = 76;
  const tapToggleZoneHeight = 44;
  const footerListeners = new WeakSet();
  const footerDrags = new WeakMap();

  const getFooter = (root) => root.closest('.cart-drawer__footer');
  const getRootFromFooter = (footer) => footer.querySelector('#ot-dingdoong-root-cart-drawer');
  const getDrawerBody = (root) => {
    return root.closest('.drawer__content')?.querySelector('.drawer__body.flex-grow:not([id*="Empty"])');
  };

  const cssLengthToPx = (value, fallback) => {
    const trimmed = String(value || '').trim();
    const number = parseFloat(trimmed);
    if (!Number.isFinite(number)) return fallback;
    if (trimmed.endsWith('rem')) {
      return number * parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
    }
    return number;
  };

  const getLimits = (root) => {
    const style = getComputedStyle(root);
    const collapsed = cssLengthToPx(style.getPropertyValue('--fte-delivery-sheet-collapsed-height'), 0);
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 720;
    const drawer = root.closest('cart-drawer');
    const header = drawer?.querySelector('.drawer__header');
    const footer = getFooter(root);
    const drawerBody = getDrawerBody(root);
    const headerHeight = header?.getBoundingClientRect().height || 0;
    const rootHeight = root.getBoundingClientRect().height || 0;
    // footer.getBoundingClientRect().height kapt af op de zichtbare hoogte; als de
    // Bestellen/GPay-knoppen onder de viewport uitsteken telt die niet mee. scrollHeight
    // pakt de volledige content-hoogte, zodat de knop-ruimte wel gereserveerd wordt.
    const footerHeight = footer ? Math.max(footer.getBoundingClientRect().height, footer.scrollHeight) : 0;
    const footerStaticHeight = Math.max(0, footerHeight - rootHeight);
    const bodyMinHeight = cssLengthToPx(getComputedStyle(drawerBody || document.documentElement).minHeight, 176);
    const safeGap = 12;
    const availableHeight = viewportHeight - headerHeight - footerStaticHeight - bodyMinHeight - safeGap;
    const viewportCap = Math.max(0, Math.min(viewportHeight * 0.42, availableHeight));
    const minimumExpanded = Math.min(220, Math.max(140, viewportHeight * 0.22));
    // expanded = de MAXIMALE hoogte die de checkout-knoppen zichtbaar houdt (de cap).
    // Het sheet zelf sluit via CSS (height:auto) op zijn eigen content tot aan deze cap.
    // We meten de content hier bewust niet: een te vroege meting (footer nog niet
    // gerenderd, widget nog niet klaar) gaf eerder een te hoog sheet met witruimte.
    const expanded = Math.max(collapsed, Math.min(availableHeight, Math.max(minimumExpanded, viewportCap)));
    return { collapsed, expanded };
  };

  const setSheetHeight = (root, height) => {
    root.style.setProperty('--fte-delivery-sheet-height', `${Math.round(height)}px`);
  };

  const setSheetState = (root, state, limits = getLimits(root)) => {
    const drawer = root.closest('cart-drawer');
    if (!drawer) return;

    const isExpanded = state !== 'collapsed';
    root.dataset.fteDeliverySheetState = isExpanded ? 'expanded' : 'collapsed';
    drawer.classList.toggle(expandedClass, isExpanded);
    drawer.classList.toggle(collapsedClass, !isExpanded);
    setSheetHeight(root, isExpanded ? limits.expanded : limits.collapsed);
  };

  const resetSheet = (root) => {
    const drawer = root.closest('cart-drawer');
    if (drawer) {
      drawer.classList.remove(expandedClass, collapsedClass, draggingClass);
    }
    root.style.removeProperty('--fte-delivery-sheet-height');
  };

  const isInteractiveTarget = (target) => {
    return Boolean(target.closest('button, a, input, select, textarea, label, summary, details'));
  };

  const updateDragHeight = (drag) => {
    drag.raf = 0;
    setSheetHeight(drag.root, drag.height);
  };

  const beginDrag = (footer, target, clientY, kind, pointerId) => {
    if (footerDrags.has(footer)) return;
    const root = getRootFromFooter(footer);
    const drawer = root?.closest('cart-drawer');
    if (!mobileMedia.matches || !root || !drawer) return;
    if (isInteractiveTarget(target)) return;

    const footerTop = footer.getBoundingClientRect().top;
    if (clientY > footerTop + dragZoneHeight) return;

    const limits = getLimits(root);
    const startHeight = root.getBoundingClientRect().height;
    footerDrags.set(footer, {
      root,
      drawer,
      kind,
      pointerId,
      startY: clientY,
      lastY: clientY,
      startHeight,
      height: startHeight,
      moved: false,
      canTapToggle: clientY <= footerTop + tapToggleZoneHeight && !target.closest('#ot-dingdoong-root-cart-drawer'),
      limits,
      raf: 0,
    });

    drawer.classList.add(draggingClass);
    setSheetHeight(root, startHeight);
  };

  const moveDragTo = (footer, clientY) => {
    const drag = footerDrags.get(footer);
    if (!drag) return false;

    drag.lastY = clientY;
    const delta = drag.startY - clientY;
    if (Math.abs(delta) > 4) drag.moved = true;
    drag.height = Math.min(Math.max(drag.startHeight + delta, drag.limits.collapsed), drag.limits.expanded);

    if (!drag.raf) {
      drag.raf = window.requestAnimationFrame(() => updateDragHeight(drag));
    }
    return true;
  };

  const finishDrag = (footer, cancelled) => {
    const drag = footerDrags.get(footer);
    if (!drag) return;

    if (drag.raf) {
      window.cancelAnimationFrame(drag.raf);
      updateDragHeight(drag);
    }

    footerDrags.delete(footer);
    drag.drawer.classList.remove(draggingClass);

    const currentState = drag.root.dataset.fteDeliverySheetState || 'expanded';
    let nextState = currentState;

    if (drag.moved) {
      const delta = drag.startY - drag.lastY;
      const midpoint = drag.limits.expanded * 0.45;
      nextState = delta < -28 || drag.height < midpoint ? 'collapsed' : 'expanded';
    } else if (!cancelled && drag.canTapToggle) {
      nextState = currentState === 'expanded' ? 'collapsed' : 'expanded';
    }

    setSheetState(drag.root, nextState, drag.limits);
  };

  const findTouch = (touchList, touchId) => {
    for (let i = 0; i < touchList.length; i += 1) {
      if (touchList[i].identifier === touchId) return touchList[i];
    }
    return null;
  };

  const bindFooter = (footer) => {
    if (footerListeners.has(footer)) return;
    footerListeners.add(footer);

    // Touch: eigen touch-handlers met non-passive preventDefault op touchmove.
    // Pointer events alleen volstaan niet: preventDefault op pointerdown blokkeert
    // native scrollen niet, waardoor Chrome (iOS/Android) en oudere Safari's de
    // veeg als scroll claimen en de drag afbreken met pointercancel.
    const onTouchStart = (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      beginDrag(footer, touch.target, touch.clientY, 'touch', touch.identifier);
    };

    const onTouchMove = (event) => {
      const drag = footerDrags.get(footer);
      if (!drag || drag.kind !== 'touch') return;
      const touch = findTouch(event.touches, drag.pointerId);
      if (!touch) return;
      if (moveDragTo(footer, touch.clientY) && event.cancelable) {
        event.preventDefault();
      }
    };

    const onTouchEnd = (event) => {
      const drag = footerDrags.get(footer);
      if (!drag || drag.kind !== 'touch') return;
      if (!findTouch(event.changedTouches, drag.pointerId)) return;
      finishDrag(footer, event.type === 'touchcancel');
    };

    footer.addEventListener('touchstart', onTouchStart, { passive: true });
    footer.addEventListener('touchmove', onTouchMove, { passive: false });
    footer.addEventListener('touchend', onTouchEnd);
    footer.addEventListener('touchcancel', onTouchEnd);

    // Muis (smal desktop-venster / responsive mode): pointer events, maar alleen
    // voor pointerType 'mouse' zodat touch niet dubbel afgehandeld wordt.
    const onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      beginDrag(footer, event.target, event.clientY, 'mouse', event.pointerId);
      if (footerDrags.get(footer)?.kind === 'mouse') {
        try {
          footer.setPointerCapture?.(event.pointerId);
        } catch (error) {}
        event.preventDefault();
      }
    };

    const onPointerMove = (event) => {
      const drag = footerDrags.get(footer);
      if (!drag || drag.kind !== 'mouse' || event.pointerId !== drag.pointerId) return;
      moveDragTo(footer, event.clientY);
      event.preventDefault();
    };

    const onPointerEnd = (event) => {
      const drag = footerDrags.get(footer);
      if (!drag || drag.kind !== 'mouse' || event.pointerId !== drag.pointerId) return;
      finishDrag(footer, event.type === 'pointercancel');
      try {
        footer.releasePointerCapture?.(event.pointerId);
      } catch (error) {}
    };

    footer.addEventListener('pointerdown', onPointerDown);
    footer.addEventListener('pointermove', onPointerMove);
    footer.addEventListener('pointerup', onPointerEnd);
    footer.addEventListener('pointercancel', onPointerEnd);
  };

  const syncDrawerOpenState = (root) => {
    const drawer = root.closest('cart-drawer');
    if (!drawer) return;

    const isActive = drawer.hasAttribute('active');
    const wasActive = drawer.dataset.fteDeliveryDrawerActive === 'true';
    drawer.dataset.fteDeliveryDrawerActive = isActive ? 'true' : 'false';

    // Bij elke keer openen van de drawer: standaard uitgeklapt tonen en de
    // limieten opnieuw meten (metingen terwijl de drawer verborgen was kloppen niet).
    if (isActive && !wasActive && !drawer.classList.contains(draggingClass)) {
      setSheetState(root, 'expanded');
    }
  };

  const initRoot = (root, refresh = false) => {
    if (!mobileMedia.matches) {
      resetSheet(root);
      return;
    }

    const footer = getFooter(root);
    if (!footer) return;
    bindFooter(footer);

    // De bezorgapp + footer renderen async; de meting bij openen kan te vroeg zijn
    // (footer nog niet klaar → cap te hoog → op kleine schermen valt de knop af). Een
    // ResizeObserver op de content her-meet setSheetState zodra de layout settelt.
    // Loop-veilig: setSheetState verandert de ROOT-hoogte, niet de content, dus de
    // observer vuurt daar niet opnieuw op.
    if (window.ResizeObserver) {
      const content = root.querySelector('.ot-dingdoong-main') || root.firstElementChild;
      if (content && root.fteDeliveryObservedContent !== content) {
        root.fteDeliveryResizeObserver =
          root.fteDeliveryResizeObserver ||
          new ResizeObserver(() => {
            const drawer = root.closest('cart-drawer');
            if (
              drawer?.hasAttribute('active') &&
              !drawer.classList.contains(draggingClass) &&
              (root.dataset.fteDeliverySheetState || 'expanded') === 'expanded'
            ) {
              setSheetState(root, 'expanded');
            }
          });
        root.fteDeliveryResizeObserver.disconnect();
        root.fteDeliveryResizeObserver.observe(content);
        root.fteDeliveryObservedContent = content;
      }
    }

    if (root.closest('cart-drawer')?.classList.contains(draggingClass)) return;

    if (root.dataset.fteDeliverySheetReady === 'true' && !refresh) {
      syncDrawerOpenState(root);
      return;
    }

    root.dataset.fteDeliverySheetReady = 'true';
    setSheetState(root, root.dataset.fteDeliverySheetState || 'expanded');
    syncDrawerOpenState(root);
  };

  const initSheets = (refresh = false) => {
    document.querySelectorAll(rootSelector).forEach((root) => initRoot(root, refresh));
  };

  let initFrame = 0;
  const scheduleInit = () => {
    if (initFrame) return;
    initFrame = window.requestAnimationFrame(() => {
      initFrame = 0;
      initSheets(false);
    });
  };

  const observer = new MutationObserver(scheduleInit);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['active', 'hidden', 'open'],
  });

  document.addEventListener('DOMContentLoaded', () => initSheets(false));
  window.addEventListener('resize', () => initSheets(true));
  mobileMedia.addEventListener?.('change', () => initSheets(true));
  initSheets(false);
})();
